// src/pages/ContactPage.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../config/apiKey.js";

export default function ContactPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get("content-type");
      let data = {};
      
      if (contentType && contentType.includes("application/json")) {
        try {
          data = await response.json();
        } catch (parseError) {
          console.error("Failed to parse JSON response:", parseError);
          throw new Error("Server returned invalid response. Please try again later.");
        }
      } else {
        // Response is HTML (likely a 502 error page)
        const text = await response.text();
        console.error("Non-JSON response received:", text.substring(0, 200));
        if (response.status === 502 || response.status === 503) {
          throw new Error("Service temporarily unavailable. Please try again later.");
        }
        throw new Error("Server error. Please try again later.");
      }

      if (!response.ok) {
        const errorMsg = data.error || data.message || "Failed to send message";
        throw new Error(typeof errorMsg === "string" ? errorMsg : "Failed to send message");
      }

      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error("Contact form error:", err);
      // Ensure error is always a string
      const errorMessage = err instanceof Error 
        ? err.message 
        : (typeof err === "string" ? err : "Failed to send message. Please try again.");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Subtle Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(#1e3a8a 0.5px, transparent 0.5px),
            radial-gradient(#1e3a8a 0.5px, #ffffff 0.5px)
          `,
          backgroundSize: "40px 40px",
          backgroundPosition: "0 0, 20px 20px",
          opacity: 0.03,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          position: "relative",
          zIndex: 10,
        }}
      >
        {/* Logo */}
        <Box
          onClick={() => navigate("/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "rgba(30, 58, 138, 0.05)",
            borderRadius: 999,
            px: 2.5,
            py: 0.75,
            border: "1px solid rgba(30, 58, 138, 0.1)",
            cursor: "pointer",
            "&:hover": { bgcolor: "rgba(30, 58, 138, 0.1)" },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#1e3a8a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "20px" }}>
              EXI
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: "#1e3a8a",
              fontWeight: "bold",
              fontSize: "22px",
            }}
          >
            exivis
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
          <Button
            onClick={() => navigate("/")}
            sx={{
              color: "#1e3a8a",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "16px",
              "&:hover": { bgcolor: "rgba(30, 58, 138, 0.05)" },
            }}
          >
            Home
          </Button>
          <Button
            onClick={() => navigate("/about")}
            sx={{
              color: "#1e3a8a",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "16px",
              "&:hover": { bgcolor: "rgba(30, 58, 138, 0.05)" },
            }}
          >
            About
          </Button>
          <Button
            sx={{
              color: "#2563eb",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "16px",
              borderBottom: "2px solid #2563eb",
              borderRadius: 0,
              "&:hover": { bgcolor: "rgba(30, 58, 138, 0.05)" },
            }}
          >
            Contact
          </Button>
          <Button
            onClick={() => navigate("/login")}
            variant="contained"
            sx={{
              bgcolor: "#1e3a8a",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "16px",
              px: 3,
              borderRadius: 999,
              "&:hover": {
                bgcolor: "#1e40af",
              },
            }}
          >
            Login
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          position: "relative",
          zIndex: 10,
          py: 6,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                fontWeight: 800,
                color: "#1e3a8a",
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              Get in{" "}
              <Box component="span" sx={{ color: "#2563eb" }}>
                Touch
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#475569",
                fontSize: "18px",
                maxWidth: "600px",
                mx: "auto",
                lineHeight: 1.8,
                fontWeight: 500,
              }}
            >
              Have a question or need help? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 4,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05)",
            }}
          >
            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3,
                  bgcolor: "#f0fdf4",
                  color: "#166534",
                  border: "1px solid #bbf7d0",
                }}
              >
                Thank you for your message! We'll get back to you soon.
              </Alert>
            )}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  bgcolor: "#fef2f2",
                  color: "#991b1b",
                  border: "1px solid #fee2e2",
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                multiline
                rows={6}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  bgcolor: "#1e3a8a",
                  color: "white",
                  py: 1.5,
                  fontSize: "16px",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "12px",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#1e40af",
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Send Message"}
              </Button>
            </form>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}




